import React, { useEffect, useMemo, useRef } from 'react';
import { Progress, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table';

import { IIconMatchResult } from '../../../../utils/asset-type-import';
import { AssetIcon } from '../../../lib/asset-icon/AssetIcon';
import SecondaryButton from '../../../lib/secondary-button';
import { IIconOutcome, IIconsStepProps } from './i-import-wizard';

const MAX_LISTED_CODES = 10;

const TAG_BASE: React.CSSProperties = {
  margin: 0,
  borderRadius: 4,
  fontSize: 12,
  lineHeight: '20px',
};

const NEW_TAG: React.CSSProperties = {
  ...TAG_BASE,
  background: '#f6ffed',
  border: '1px solid #b7eb8f',
  color: '#52c41a',
};

const ERROR_TAG: React.CSSProperties = {
  ...TAG_BASE,
  background: '#fdecec',
  border: '1px solid #f4b8b8',
  color: '#e13333',
};

const GREY_TAG: React.CSSProperties = {
  ...TAG_BASE,
  background: '#fafafa',
  border: '1px solid #d9d9d9',
  color: '#8c909b',
};

const headerCellStyle: React.CSSProperties = {
  padding: '17px 10px',
  background: '#fafafa',
  fontSize: 13,
  fontWeight: 600,
  color: 'rgba(0, 0, 0, 0.85)',
};

const bodyCellStyle: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: 13,
  color: '#535559',
};

const hintStyle: React.CSSProperties = { fontSize: 12, color: '#8C909B' };

const tableWrapStyle: React.CSSProperties = {
  border: '1px solid #f1f2f5',
  borderRadius: 11,
  overflow: 'hidden',
};

const cellProps = {
  onHeaderCell: (): { style: React.CSSProperties } => ({ style: headerCellStyle }),
  onCell: (): { style: React.CSSProperties } => ({ style: bodyCellStyle }),
};

type TIconRowKind = 'matched' | 'unmatched' | 'rejected';

interface IIconRow {
  key: string;
  name: string;
  code: string | null;
  kind: TIconRowKind;
  reason: string;
  url: string | null;
}

const buildRows = (match: IIconMatchResult | null): IIconRow[] => {
  if (!match) return [];

  return [
    ...match.matched.map((item, index) => ({
      key: `matched-${index}-${item.file.name}`,
      name: item.file.name,
      code: item.code,
      kind: 'matched' as TIconRowKind,
      reason: '',
      url: URL.createObjectURL(item.file),
    })),
    ...match.unmatched.map((file, index) => ({
      key: `unmatched-${index}-${file.name}`,
      name: file.name,
      code: null,
      kind: 'unmatched' as TIconRowKind,
      reason: '',
      url: URL.createObjectURL(file),
    })),
    ...match.rejected.map((item, index) => ({
      key: `rejected-${index}-${item.file.name}`,
      name: item.file.name,
      code: null,
      kind: 'rejected' as TIconRowKind,
      reason: item.reason,
      url: null,
    })),
  ];
};

const renderPreview = (row: IIconRow): JSX.Element => (
  <div style={{ display: 'flex', justifyContent: 'center' }}>
    {row.url
      ? <AssetIcon src={row.url} size={28} />
      : <span style={{ color: '#B5B8BF' }}>—</span>}
  </div>
);

const renderStatus = (row: IIconRow): JSX.Element => {
  if (row.kind === 'matched') return <Tag style={NEW_TAG}>Ready</Tag>;

  if (row.kind === 'unmatched') return <Tag style={GREY_TAG}>No matching code</Tag>;

  return (
    <div>
      <Tag style={ERROR_TAG}>Rejected</Tag>
      {row.reason ? (
        <div style={{ fontSize: 12, marginTop: 2, color: '#E13333' }}>{row.reason}</div>
      ) : null}
    </div>
  );
};

const columns: ColumnsType<IIconRow> = [
  {
    title: 'Preview',
    key: 'preview',
    width: 64,
    align: 'center',
    render: (_: unknown, row: IIconRow) => renderPreview(row),
    ...cellProps,
  },
  {
    title: 'File',
    dataIndex: 'name',
    key: 'name',
    ...cellProps,
  },
  {
    title: 'Code',
    key: 'code',
    width: 200,
    render: (_: unknown, row: IIconRow) => row.code ?? <span style={{ color: '#B5B8BF' }}>—</span>,
    ...cellProps,
  },
  {
    title: 'Status',
    key: 'status',
    width: 260,
    render: (_: unknown, row: IIconRow) => renderStatus(row),
    ...cellProps,
  },
];

const failureColumns: ColumnsType<IIconOutcome> = [
  {
    title: 'File',
    dataIndex: 'fileName',
    key: 'fileName',
    ...cellProps,
  },
  {
    title: 'Code',
    dataIndex: 'code',
    key: 'code',
    width: 200,
    ...cellProps,
  },
  {
    title: 'Reason',
    key: 'reason',
    render: (_: unknown, outcome: IIconOutcome) => outcome.reason ?? '',
    ...cellProps,
  },
];

const missingIconsText = (codes: string[]): string => {
  const listed = codes.slice(0, MAX_LISTED_CODES).join(', ');
  const rest = codes.length - MAX_LISTED_CODES;
  const tail = rest > 0 ? `, …and ${rest} more` : '';

  return `${codes.length} asset types have no icon file: ${listed}${tail}`;
};

const IconsStep = (props: IIconsStepProps): JSX.Element => {
  const {
    phase,
    match,
    progress,
    outcomes,
    supportsDirectory,
    disabled,
    onFilesSelected,
  } = props;

  const filesInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const rows = useMemo(() => buildRows(match), [match]);

  useEffect(() => () => {
    rows.forEach((row) => {
      if (row.url) URL.revokeObjectURL(row.url);
    });
  }, [rows]);

  useEffect(() => {
    const node = folderInputRef.current;
    if (!node) return;

    node.setAttribute('webkitdirectory', '');
    node.setAttribute('directory', '');
  }, [supportsDirectory, phase]);

  const handleFiles = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const input = event.target;

    onFilesSelected(Array.from(input.files ?? []));
    input.value = '';
  };

  const pickersDisabled = disabled || phase === 'uploading';
  const showTable = phase !== 'done';
  const percent = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  const uploadedCount = outcomes.filter((outcome) => outcome.ok).length;
  const failures = outcomes.filter((outcome) => !outcome.ok);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {phase === 'done' ? null : (
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            ref={filesInputRef}
            type="file"
            multiple
            accept="image/png,image/jpeg,image/webp"
            style={{ display: 'none' }}
            onChange={handleFiles}
          />
          <SecondaryButton
            type="button"
            disabled={pickersDisabled}
            onClick={() => filesInputRef.current?.click()}
          >
            Choose files
          </SecondaryButton>

          {supportsDirectory ? (
            <>
              <input
                ref={folderInputRef}
                type="file"
                multiple
                accept="image/png,image/jpeg,image/webp"
                style={{ display: 'none' }}
                onChange={handleFiles}
              />
              <SecondaryButton
                type="button"
                disabled={pickersDisabled}
                onClick={() => folderInputRef.current?.click()}
              >
                Choose folder
              </SecondaryButton>
            </>
          ) : null}
        </div>
      )}

      {showTable && !match ? (
        <div style={hintStyle}>No files selected yet.</div>
      ) : null}

      {showTable && match ? (
        <div style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8,
        }}
        >
          {match.matched.length > 0 ? (
            <Tag style={NEW_TAG}>{`${match.matched.length} ready`}</Tag>
          ) : null}
          {match.unmatched.length > 0 ? (
            <Tag style={GREY_TAG}>{`${match.unmatched.length} with no matching code`}</Tag>
          ) : null}
          {match.rejected.length > 0 ? (
            <Tag style={ERROR_TAG}>{`${match.rejected.length} rejected`}</Tag>
          ) : null}
          {match.withoutIcon.length > 0 ? (
            <Tag style={GREY_TAG}>{`${match.withoutIcon.length} codes without an icon`}</Tag>
          ) : null}
        </div>
      ) : null}

      {showTable && rows.length > 0 ? (
        <div style={tableWrapStyle}>
          <Table
            size="small"
            rowKey="key"
            columns={columns}
            dataSource={rows}
            pagination={{ pageSize: 10, size: 'small', hideOnSinglePage: true }}
            scroll={{ y: 300 }}
          />
        </div>
      ) : null}

      {showTable && match && match.withoutIcon.length > 0 ? (
        <div style={hintStyle}>{missingIconsText(match.withoutIcon)}</div>
      ) : null}

      {phase === 'uploading' ? (
        <div>
          <Progress percent={percent} status="active" />
          <div style={hintStyle}>{`Uploading ${progress.done} of ${progress.total}…`}</div>
        </div>
      ) : null}

      {phase === 'done' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 14, color: '#52c41a' }}>
            {`${uploadedCount} icons uploaded`}
          </div>

          {failures.length > 0 ? (
            <>
              <div>
                <Tag style={ERROR_TAG}>{`${failures.length} failed`}</Tag>
              </div>
              <div style={tableWrapStyle}>
                <Table
                  size="small"
                  rowKey={(outcome) => `${outcome.code}-${outcome.fileName}`}
                  columns={failureColumns}
                  dataSource={failures}
                  pagination={{ pageSize: 10, size: 'small', hideOnSinglePage: true }}
                />
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      <div style={hintStyle}>
        Files are matched to asset types by file name. PNG, JPEG or WebP up to 512 KB; the server
        resizes every icon to a 256×256 PNG. Icons of rows skipped as duplicates are never touched.
      </div>
    </div>
  );
};

IconsStep.displayName = 'IconsStep';

export default IconsStep;
